import { Button, ButtonColors } from '@chiroup/components/Button';
import { Card } from '@chiroup/components/Card';
import { DateRangePicker } from '@chiroup/components/DateRangePicker';
import { ShortcutOption } from '@chiroup/components/date-picker-types';
import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import { ChiroUpBaseCommon, ChiroUpDayJsCommon } from '@chiroup/core/constants/stringConstants';
import { AdHocColumnFormat, AdHocReportDescriptor } from '@chiroup/core/types/AdHocReport.type';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { unparse } from 'papaparse';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AdHocRunHistory from './AdHocRunHistory';
import useAdHocReport from './useAdHocReport';

const reportingShortcuts: ShortcutOption[] = ['thisMonth', 'lastMonth', 'thisYear', 'lastYear'];

type Props = {
  descriptor: AdHocReportDescriptor;
  clinicId: number;
};

const dateFormatMap: Record<string, string> = {
  date: ChiroUpDayJsCommon.format.date,
  isoDate: ChiroUpDayJsCommon.format.isoDate,
  mmddyyyy: ChiroUpDayJsCommon.format.mmddyyyy,
  dateTime: ChiroUpDayJsCommon.format.datetime,
  datetimeWithDay: ChiroUpDayJsCommon.format.datetimeWithDay,
  timestamp: ChiroUpDayJsCommon.format.timestamp,
  time: ChiroUpDayJsCommon.format.time,
};

const formatCellValue = (value: any, format?: AdHocColumnFormat) => {
  if (value == null || !format || format === 'string') return value;

  if (format === 'currency') {
    return ChiroUpBaseCommon.format.asMoney(Number(value));
  }

  const dayjsFmt = dateFormatMap[format];

  if (dayjsFmt) {
    return ChiroUpDayJsCommon.display.datetime(value, undefined, dayjsFmt);
  }

  return value;
};

const AdHocReportCard = ({ descriptor, clinicId }: Props) => {
  const {
    runs,
    isLoadingRuns,
    runReport,
    isRunning,
    resultData,
    storedColumns,
    activeRun,
    loadRunResults,
    fetchingRunId,
    deleteRun,
    deletingRunIds,
    clearResults,
  } = useAdHocReport(descriptor, clinicId);

  const effectiveColumns = storedColumns ?? descriptor.columns;

  const [reportName, setReportName] = useState('');

  const [filterValues, setFilterValues] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {};

    for (const filter of descriptor.filters) {
      if (filter.type !== 'dateRange' && typeof filter.key === 'string') {
        initial[filter.key] = filter.defaultValue ?? '';
      }
    }

    return initial;
  });

  const defaultDateRange = useMemo(() => {
    const dateFilter = descriptor.filters.find(f => f.type === 'dateRange');

    if (dateFilter?.defaultValue) {
      const dv = dateFilter.defaultValue as { from: string; to: string };

      return { from: dv.from, to: dv.to };
    }

    return { from: '', to: '' };
  }, [descriptor.filters]);

  const [dateRange, setDateRange] = useState(defaultDateRange);

  const defaultReportName = useMemo(() => {
    const allFilters: Record<string, unknown> = {
      ...filterValues,
      dateRange,
    };

    if (descriptor.defaultReportName) {
      return descriptor.defaultReportName({ name: descriptor.name, filters: allFilters });
    }

    const from = dateRange.from ? dayjs(dateRange.from).format('MM/DD/YYYY') : '';
    const to = dateRange.to ? dayjs(dateRange.to).format('MM/DD/YYYY') : '';

    if (from && to) return `${descriptor.name} ${from} - ${to}`;
    if (from || to) return `${descriptor.name} ${from || to}`;

    return descriptor.name;
  }, [descriptor, dateRange, filterValues]);

  const hasRequiredFilters = useMemo(() => {
    for (const filter of descriptor.filters) {
      if (!filter.required) continue;

      if (filter.type === 'dateRange') {
        if (!dateRange.from || !dateRange.to) return false;
      } else if (typeof filter.key === 'string') {
        const val = filterValues[filter.key];

        if (val === undefined || val === '' || val === null || val === false) return false;
      }
    }

    return true;
  }, [descriptor.filters, dateRange, filterValues]);

  const handleRun = async () => {
    if (!hasRequiredFilters) return;

    const name = reportName.trim() || defaultReportName;

    const params: Record<string, unknown> = {};

    for (const filter of descriptor.filters) {
      if (filter.type === 'dateRange' && typeof filter.key === 'object') {
        params[filter.key.from] = dateRange.from;
        params[filter.key.to] = dateRange.to;
      } else if (typeof filter.key === 'string') {
        const val = filterValues[filter.key];

        if (val !== undefined && val !== '' && val !== null && val !== false) {
          params[filter.key] = val;
        }
      }
    }

    try {
      await runReport({ name, params, columns: descriptor.columns });
    } catch {
      // Error already handled by mutation's onError callback (toast + query invalidation).
    }
  };

  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(
    () => new Set(descriptor.columns.filter(c => c.defaultVisible).map(c => c.key))
  );

  useEffect(() => {
    setVisibleKeys(new Set(effectiveColumns.filter(c => c.defaultVisible).map(c => c.key)));
  }, [effectiveColumns]);

  const toggleColumn = useCallback((key: string) => {
    setVisibleKeys(prev => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }, []);

  const visibleColumns = useMemo(
    () => effectiveColumns.filter(c => visibleKeys.has(c.key)),
    [effectiveColumns, visibleKeys]
  );

  const handleDownloadCsv = useCallback(() => {
    if (!resultData?.length) return;

    const formatLookup = new Map(
      visibleColumns.filter(c => c.format && c.format !== 'string').map(c => [c.key, c.format])
    );

    const keys = visibleColumns.map(c => c.key);

    const csvRows = resultData.map(row => {
      const picked: any = {};

      for (const key of keys) {
        const fmt = formatLookup.get(key);
        picked[key] = fmt ? formatCellValue(row[key], fmt) : row[key];
      }

      return picked;
    });

    const csv = unparse(csvRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `${descriptor.csvFileNamePrefix}-${dayjs().format('YYYY-MM-DD')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [resultData, visibleColumns, descriptor.csvFileNamePrefix]);

  const tableColumns = useMemo<ColumnDef<Record<string, unknown>, unknown>[]>(() => {
    return visibleColumns.map(col => {
      const descriptorCol = descriptor.columns.find(c => c.key === col.key);
      const hrefFn = descriptorCol?.href;

      const column: ColumnDef<Record<string, unknown>, unknown> = {
        accessorKey: col.key,
        header: col.label,
        enableSorting: false,
        meta: {
          className: col.tdClassName || 'whitespace-nowrap',
        },
      };

      if (hrefFn) {
        const formatFn = col.format && col.format !== 'string' ? col.format : null;

        column.cell = ({ row }) => {
          const value = row.original[col.key];
          const display = formatFn ? formatCellValue(value, formatFn) : value;
          const url = hrefFn(row.original);

          return (
            <span className="inline-flex items-center gap-1">
              {display as React.ReactNode}
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ArrowTopRightOnSquareIcon className="h-4 w-4 text-primary-600 hover:text-primary-800" />
              </a>
            </span>
          );
        };
      } else if (col.format && col.format !== 'string') {
        column.cell = ({ row }) => {
          return formatCellValue(row.original[col.key], col.format) as React.ReactNode;
        };
      }

      return column;
    });
  }, [visibleColumns, descriptor.columns]);

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">{descriptor.name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{descriptor.description}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            {resultData && (
              <>
                <Button text="Clear" onClick={clearResults} color={ButtonColors.plainWithBorder} />
                <Button
                  text="Download"
                  onClick={handleDownloadCsv}
                  color={ButtonColors.plainWithBorder}
                />
              </>
            )}
            <Button
              text="Run"
              onClick={handleRun}
              loading={isRunning}
              disabled={isRunning || !hasRequiredFilters}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-1">
            <label className="block text-sm font-medium mb-1">Report Name (optional)</label>
            <input
              type="text"
              value={reportName}
              onChange={e => setReportName(e.target.value)}
              placeholder={defaultReportName}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-gray-800 px-3 py-2 text-sm"
            />
          </div>

          {descriptor.filters.map(filter => {
            if (filter.type === 'dateRange') {
              return (
                <div key={filter.label} className="sm:col-span-1">
                  <DateRangePicker
                    label={filter.label}
                    className="h-10"
                    value={dateRange}
                    onChange={e => {
                      if (e) {
                        setDateRange({
                          from: e.from ? dayjs(e.from).format('YYYY-MM-DD') : '',
                          to: e.to ? dayjs(e.to).format('YYYY-MM-DD') : '',
                        });
                      }
                    }}
                    shortcuts={reportingShortcuts}
                    clearable
                  />
                </div>
              );
            }

            if (filter.type === 'select' && typeof filter.key === 'string' && filter.options) {
              const filterKey = filter.key;

              return (
                <div key={filter.label} className="sm:col-span-1">
                  <label className="block text-sm font-medium mb-1">{filter.label}</label>
                  <select
                    value={String(filterValues[filterKey] ?? '')}
                    onChange={e => {
                      setFilterValues(prev => ({
                        ...prev,
                        [filterKey]: e.target.value || '',
                      }));
                    }}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-gray-800 px-3 py-2 text-sm h-10"
                  >
                    <option value="">{filter.placeholder || 'All'}</option>
                    {filter.options.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }

            if (filter.type === 'toggle' && typeof filter.key === 'string') {
              const filterKey = filter.key;

              return (
                <div key={filter.label} className="sm:col-span-1 flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer pb-2">
                    <input
                      type="checkbox"
                      checked={!!filterValues[filterKey]}
                      onChange={e => {
                        setFilterValues(prev => ({
                          ...prev,
                          [filterKey]: e.target.checked,
                        }));
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    {filter.label}
                  </label>
                </div>
              );
            }

            return null;
          })}
        </div>

        {activeRun && resultData && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {activeRun.name} &mdash; {resultData.length} row
                {resultData.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
              {effectiveColumns.map(col => (
                <label
                  key={col.key}
                  className="inline-flex items-center gap-1.5 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={visibleKeys.has(col.key)}
                    onChange={() => toggleColumn(col.key)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  {col.label}
                </label>
              ))}
            </div>
            <DataTable
              columns={tableColumns}
              data={resultData}
              pagination={{ pageSize: 10 }}
              size="default"
            />
          </div>
        )}

        <AdHocRunHistory
          runs={runs}
          isLoading={isLoadingRuns}
          onViewRun={loadRunResults}
          fetchingRunId={fetchingRunId}
          onDeleteRun={deleteRun}
          deletingRunIds={deletingRunIds}
        />
      </div>
    </Card>
  );
};

export default AdHocReportCard;
